import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatch } from "react-redux";
import { connectUser } from "../../store/Store";

import Signup from './Signup';
import ForgotPassword from './ForgotPassword';
import Logo from '../../assets/logo.png';

import "../../styles/Landing.css";
import "../../styles/authentification/Login.css";

export default function Login({ onLogin }) {
  axios.defaults.baseURL = "http://localhost:8080";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const [soonModal, setSoonModal] = useState({ open: false, label: null });
  const openSoon = (label) => setSoonModal({ open: true, label });
  const closeSoon = () => setSoonModal({ open: false, label: null });

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  useEffect(() => {
    const wantSignup = query.get("signup");
    if (wantSignup === "1" || wantSignup === "true") {
      setIsSignup(true);
      setIsForgotPassword(false);
    }
  }, [query]);

  const handleLogin = () => {
    axios.post("/api/v1/auth/login", { email, password })
      .then((response) => {
        const data = response.data;

        dispatch(connectUser({
          id: data.id,
          email: data.email
        }));

        setErrorMessage(null);
        onLogin?.();
        navigate("/home");
      })
      .catch((error) => {
        setErrorMessage("Email ou mot de passe invalide");
        console.error("Erreur login:", error);
      });
  };

  const toggleShowPassword = () => setShowPassword(v => !v);
  const onKeyDown = (e) => { if (e.key === 'Enter') handleLogin(); };

  const onTermsClick = (e) => { e.preventDefault(); openSoon("Conditions d’utilisation"); };
  const onPrivacyClick = (e) => { e.preventDefault(); openSoon("Politique de confidentialité (RGPD)"); };
  const onSecurityClick = (e) => { e.preventDefault(); openSoon("Sécurité"); };

  const onHeaderSignupClick = (e) => {
    e.preventDefault();
    setIsSignup(true);
    setIsForgotPassword(false);
  };

  return (
    <>
      <div className="alx-bg-scrim" aria-hidden="true" />

      <header className="alx-header">
        <a href="/landing" className="alx-brand" aria-label="Arenalyx – Accueil">
          <svg className="alx-logo" viewBox="0 0 64 64" aria-hidden="true">
            <path d="M32 6l18 30H14L32 6z" />
            <path d="M22 40h20l-10 18-10-18z" />
          </svg>
          <span className="alx-brand-text">CirculumAI</span>
        </a>

        <nav className="alx-nav" aria-label="Navigation principale">
          <a href="/landing">Accueil</a>
          <a className="alx-login" href="#" onClick={onHeaderSignupClick}>
            SignUp
          </a>
        </nav>
      </header>

      <div className="arena-login">
        <div className={`arena-card ${isSignup || isForgotPassword ? "move-transition" : ""}`}>
          <img src={Logo} alt="Logo CirculumAI" className="arena-logo" />
          <h1 className="arena-brand">CirculumAI</h1>

          {isSignup ? (
            <Signup onLogin={onLogin} />
          ) : isForgotPassword ? (
            <ForgotPassword />
          ) : (
            <>
              <h2 className="arena-title">Se connecter</h2>

              <label className="arena-label" htmlFor="email">Adresse e-mail</label>
              <input
                id="email"
                type="email"
                placeholder="Adresse e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={onKeyDown}
                className="arena-input"
                autoComplete="email"
              />

              <label className="arena-label" htmlFor="password">Mot de passe</label>
              <div className="arena-password">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={onKeyDown}
                  className="arena-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="arena-eye"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  onClick={toggleShowPassword}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>

              {errorMessage && <p className="arena-error">{errorMessage}</p>}

              <button className="arena-primary" onClick={handleLogin}>
                Se connecter
              </button>

              <div className="arena-divider"><span>ou</span></div>

              <button type="button" className="arena-sso" onClick={() => openSoon("Authentification Google")}>
                <GoogleLogo className="arena-sso-svg" />
                <span>Se connecter avec Google</span>
              </button>
              <button type="button" className="arena-sso" onClick={() => openSoon("Authentification Microsoft")}>
                <MicrosoftLogo className="arena-sso-svg" />
                <span>Se connecter avec Microsoft</span>
              </button>

              <div className="arena-links">
                <a onClick={() => setIsForgotPassword(true)}>Mot de passe oublié ?</a>
                <a onClick={() => { setIsSignup(true); setIsForgotPassword(false); }}>Première connexion</a>
              </div>
            </>
          )}

          {(isSignup || isForgotPassword) && (
            <a
              className="arena-back"
              onClick={() => { setIsSignup(false); setIsForgotPassword(false); }}
            >
              Retour
            </a>
          )}
        </div>

        <footer className="arena-footer">
          <div className="arena-footer-inner">
            <div className="arena-footer-brand">
              <span className="arena-footer-logo" />
              <span>CirculumAI</span>
            </div>
            <nav className="arena-footer-nav" aria-label="Liens légaux">
              <a href="/legal/terms" onClick={onTermsClick}>Conditions</a>
              <a href="/legal/privacy" onClick={onPrivacyClick}>Confidentialité</a>
              <a href="/legal/security" onClick={onSecurityClick}>Sécurité</a>
            </nav>
            <span className="arena-footnote">
              © {new Date().getFullYear()} CirculumAI. Tous droits réservés.
            </span>
          </div>
        </footer>
      </div>

      {soonModal.open && (
        <div className="arena-modal" role="dialog" aria-modal="true" aria-labelledby="soon-title">
          <div className="arena-modal-box">
            <h3 id="soon-title">{soonModal.label}</h3>
            <p>Cette fonctionnalité sera disponible très bientôt. Merci pour votre patience 🙏</p>
            <button className="arena-primary arena-modal-close" onClick={closeSoon}>
              OK
            </button>
          </div>
          <button className="arena-modal-backdrop" aria-label="Fermer" onClick={closeSoon} />
        </div>
      )}
    </>
  );
}

function GoogleLogo({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 31.8 29.3 35 24 35c-7 0-12.7-5.7-12.7-12.7S17 9.6 24 9.6c3.2 0 6.1 1.2 8.3 3.2l5.7-5.7C34.7 3.6 29.6 1.6 24 1.6 11.7 1.6 1.6 11.7 1.6 24S11.7 46.4 24 46.4 46.4 36.3 46.4 24c0-1.2-.1-2.4-.4-3.5Z"/>
      <path fill="#FF3D00" d="M6.3 14.3l6.6 4.8C14.8 15.3 19 12.8 24 12.8c3.2 0 6.1 1.2 8.3 3.2l5.7-5.7C34.7 6.8 29.6 4.8 24 4.8c-7.8 0-14.4 4.5-17.7 11.2z"/>
      <path fill="#4CAF50" d="M24 43.2c5.2 0 9.9-2 13.3-5.1l-6.1-5c-2 1.4-4.6 2.2-7.2 2.2-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9.5 38.3 16.2 43.2 24 43.2z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1 2.9-3 5.3-5.6 6.9l6.1 5c3.6-3.3 5.8-8.1 5.8-13.9 0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function MicrosoftLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}